/**
 * Connect funnel result page.
 *
 * Reads LeadScoreResponse from sessionStorage (set by ConnectPage on submit) and
 * shows a tailored experience based on `next_step`. Never reveals the raw score.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarCheck, Mail, Sparkles } from 'lucide-react';

import type { LeadScoreResponse } from '../types/lead';

const STORAGE_KEY = 'muleworks.connect.result';

function loadResult(): LeadScoreResponse | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LeadScoreResponse;
  } catch {
    return null;
  }
}

// Cal.com inline embed loader. Loads embed.js once and initializes a
// namespaced inline calendar pointing at booking_url.
function useCalEmbed(bookingUrl: string | null) {
  useEffect(() => {
    if (!bookingUrl) return;
    const NS = 'muleworks-connect';
    // Inject embed.js once
    if (!(window as any).Cal) {
      const script = document.createElement('script');
      script.src = 'https://app.cal.com/embed/embed.js';
      script.async = true;
      script.onload = () => initCal();
      document.head.appendChild(script);
    } else {
      initCal();
    }

    function initCal() {
      const Cal: any = (window as any).Cal;
      if (!Cal) return;
      try {
        Cal('init', NS, { origin: 'https://app.cal.com' });
        // Extract slug from booking_url. Accept either a full https://cal.com/<slug>
        // URL, or a bare slug like "aaron/intro".
        let slug: string = bookingUrl as string;
        try {
          const u = new URL(slug);
          slug = u.pathname.replace(/^\/+/, '');
        } catch {
          // not a full URL; treat as slug
        }
        Cal.ns[NS]('inline', {
          elementOrSelector: '#cal-inline-embed',
          calLink: slug,
          layout: 'month_view',
        });
        Cal.ns[NS]('ui', {
          theme: 'light',
          hideEventTypeDetails: false,
          layout: 'month_view',
        });
      } catch {
        /* ignore embed init errors */
      }
    }
  }, [bookingUrl]);
}

const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-apple-bg font-sans antialiased">
    <header className="fixed inset-x-0 top-0 z-40 bg-apple-bg/80 backdrop-blur">
      <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-6">
        <Link to="/" className="text-sm font-semibold tracking-tight text-apple-ink">
          Mule Works
        </Link>
      </div>
    </header>
    <main className="px-6 pb-section pt-24">{children}</main>
  </div>
);

const NewsletterCapture: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  return submitted ? (
    <p className="mt-6 text-sm text-apple-subtext">Thanks — you're on the list.</p>
  ) : (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        // Newsletter signup is best-effort UX-only here; backend wiring TBD.
        if (email) setSubmitted(true);
      }}
      className="mt-8 flex w-full max-w-md flex-col gap-3 sm:flex-row"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        className="flex-1 rounded-full border border-apple-divider bg-white px-5 py-3 text-sm text-apple-ink placeholder:text-apple-subtext/60 focus:border-apple-blue focus:outline-none focus:ring-0"
      />
      <button
        type="submit"
        className="rounded-full bg-apple-blue px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-apple-blueHover"
      >
        Subscribe
      </button>
    </form>
  );
};

const BookView: React.FC<{ result: LeadScoreResponse }> = ({ result }) => {
  useCalEmbed(result.booking_url);
  return (
    <div className="mx-auto max-w-3xl">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex items-center gap-3 text-apple-blue">
          <CalendarCheck className="h-5 w-5" />
          <span className="text-sm font-medium uppercase tracking-widest">
            Let's meet
          </span>
        </div>
        <h1 className="mt-4 text-4xl font-semibold tracking-tight text-apple-ink sm:text-5xl">
          Aaron has time to meet this week.
        </h1>
        <p className="mt-4 text-lg text-apple-subtext">
          {result.follow_up_message ||
            "Pick a slot that works for you — we'll send a calendar invite right away."}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mt-10 overflow-hidden rounded-3xl bg-white shadow-card"
      >
        {result.booking_url ? (
          <div id="cal-inline-embed" className="min-h-[640px] w-full" />
        ) : (
          <div className="p-10 text-apple-subtext">
            We'll reach out shortly with available times.
          </div>
        )}
      </motion.div>
    </div>
  );
};

const ThankYouView: React.FC<{ result: LeadScoreResponse }> = ({ result }) => (
  <div className="mx-auto max-w-2xl text-center">
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-card">
        <Mail className="h-5 w-5 text-apple-blue" />
      </div>
      <h1 className="mt-6 text-4xl font-semibold tracking-tight text-apple-ink sm:text-5xl">
        Thanks for sharing.
      </h1>
      <p className="mt-4 text-lg text-apple-subtext">
        {result.follow_up_message || "We'll be in touch within 48 hours."}
      </p>
      <div className="mt-10">
        <p className="text-sm text-apple-subtext">
          In the meantime, sign up for our newsletter:
        </p>
        <div className="mt-4 flex justify-center">
          <NewsletterCapture />
        </div>
      </div>
    </motion.div>
  </div>
);

const NewsletterView: React.FC<{ result: LeadScoreResponse }> = ({ result }) => (
  <div className="mx-auto max-w-2xl text-center">
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-card">
        <Sparkles className="h-5 w-5 text-apple-blue" />
      </div>
      <h1 className="mt-6 text-4xl font-semibold tracking-tight text-apple-ink sm:text-5xl">
        We'll keep you posted.
      </h1>
      <p className="mt-4 text-lg text-apple-subtext">
        {result.follow_up_message ||
          "We'll share updates as the platform grows — no spam, ever."}
      </p>
      <div className="mt-10 flex justify-center">
        <NewsletterCapture />
      </div>
    </motion.div>
  </div>
);

const ConnectResultPage: React.FC = () => {
  const result = useMemo(() => loadResult(), []);
  if (!result) {
    return <Navigate to="/connect" replace />;
  }
  return (
    <Wrapper>
      {result.next_step === 'book' ? (
        <BookView result={result} />
      ) : result.next_step === 'newsletter' ? (
        <NewsletterView result={result} />
      ) : (
        <ThankYouView result={result} />
      )}
    </Wrapper>
  );
};

export default ConnectResultPage;
